import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import auth from '../utils/auth';
import api from '../config/api';
import axiosInstance from '../services/api';
import { UserRole } from '../types';
import { registerFCMTokenWithBackend, isNotificationSupported } from '../config/firebase';
import { FaBullhorn, FaUsers, FaBlog, FaMapMarkerAlt, FaComments, FaUserShield, FaBalanceScale } from 'react-icons/fa';

const Dashboard: React.FC = () => {
  const user = auth.getUser();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isDashboardAdmin = user?.role === UserRole.DASHBOARD_ADMIN;
  const notificationRegistered = useRef(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated() || notificationRegistered.current || !isNotificationSupported()) return;
    registerFCMTokenWithBackend(auth.getAuthHeader, api.notifications.register).then((ok) => {
      if (ok) notificationRegistered.current = true;
    });
  }, []);

  const openChangePassword = () => {
    setShowProfileMenu(false);
    setShowChangePasswordModal(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordError('');
    setChangePasswordSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordError('');
    if (newPassword.length < 6) {
      setChangePasswordError('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError('كلمة المرور الجديدة وتكرارها غير متطابقتين');
      return;
    }
    setChangePasswordLoading(true);
    try {
      await axiosInstance.post(api.auth.changePassword, {
        currentPassword,
        newPassword,
      });
      setChangePasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setChangePasswordSuccess(false);
      }, 2000);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'حدث خطأ أثناء تغيير كلمة المرور';
      setChangePasswordError(typeof msg === 'string' ? msg : msg.message || 'حدث خطأ');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <div className="flex-1 flex items-center justify-start">
              <img
                src={`${process.env.PUBLIC_URL || ''}/logo.png`}
                alt="مجموعة كاف الدولية"
                className="h-10 w-auto object-contain"
              />
            </div>
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              لوحة التحكم - مجموعة كاف الدولية
            </h1>
            <div className="flex items-center gap-3 flex-1 justify-end">
              <span className="text-white text-sm opacity-90">{user?.name || user?.email}</span>
              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowProfileMenu((v) => !v)}
                  className="p-2 rounded-full text-white hover:bg-white/10 transition-colors"
                  aria-label="البروفايل"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </button>
                {showProfileMenu && (
                  <div
                    className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
                    style={{ minWidth: '180px' }}
                  >
                    <button
                      type="button"
                      onClick={openChangePassword}
                      className="w-full text-right px-4 py-2 text-gray-800 hover:bg-gray-100 text-sm"
                    >
                      تغيير كلمة المرور
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProfileMenu(false);
                        auth.removeToken();
                        window.location.href = '/login';
                      }}
                      className="w-full text-right px-4 py-2.5 text-sm font-medium rounded-b-lg border-t border-gray-100 transition-all hover:opacity-90"
                      style={{ background: 'var(--button-gradient)', color: 'var(--button-text)' }}
                    >
                      تسجيل الخروج
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <main className="p-8">
        <section className="dashboard-welcome" aria-labelledby="dashboard-heading">
          <div>
            <span className="dashboard-eyebrow">مجموعة كاف الدولية</span>
            <h2 id="dashboard-heading">أهلاً بك، {user?.name || 'مرحباً'}</h2>
            <p>من هنا يمكنك متابعة وإدارة محتوى الموقع وطلبات العملاء.</p>
          </div>
          <div className="dashboard-date">
            <span>اليوم</span>
            <strong>{new Date().toLocaleDateString('ar-EG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
          </div>
        </section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(isSuperAdmin || isDashboardAdmin) && (
            <>
              <Link
                to="/announcements"
                className="p-8 rounded-lg transition-all no-underline group"
                style={{
                  background: 'rgba(8, 42, 73, 0.6)',
                  border: '1px solid transparent',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(FaBullhorn as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                  <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>شريط الأخبار</h2>
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>إدارة الأخبار</p>
              </Link>
              <Link
                to="/team"
                className="p-8 rounded-lg transition-all no-underline group"
                style={{
                  background: 'rgba(8, 42, 73, 0.6)',
                  border: '1px solid transparent',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(FaUsers as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                  <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>فريق العمل</h2>
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>إدارة أعضاء الفريق</p>
              </Link>
              <Link
                to="/blog"
                className="p-8 rounded-lg transition-all no-underline group"
                style={{
                  background: 'rgba(8, 42, 73, 0.6)',
                  border: '1px solid transparent',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(FaBlog as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                  <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>المدونة</h2>
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>إدارة المقالات</p>
              </Link>
              <Link
                to="/news-judgments"
                className="p-8 rounded-lg transition-all no-underline group"
                style={{
                  background: 'rgba(8, 42, 73, 0.6)',
                  border: '1px solid transparent',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(FaBalanceScale as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                  <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>الأخبار والأحكام</h2>
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>مراجعة المسودات ونشرها للمدونة</p>
              </Link>
              <Link
                to="/locations"
                className="p-8 rounded-lg transition-all no-underline group"
                style={{
                  background: 'rgba(8, 42, 73, 0.6)',
                  border: '1px solid transparent',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(FaMapMarkerAlt as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                  <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>المواقع</h2>
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>إدارة المواقع (صور، عناوين، مواقع عربي/إنجليزي)</p>
              </Link>
            </>
          )}
          {(isSuperAdmin || isDashboardAdmin) && (
            <Link
              to="/consultations"
              className="p-8 rounded-lg transition-all no-underline group"
              style={{
                background: 'rgba(8, 42, 73, 0.6)',
                border: '1px solid transparent',
                color: '#ffffff',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div className="flex items-center gap-4 mb-4">
                {React.createElement(FaComments as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>الاستشارات</h2>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>إدارة طلبات الاستشارات</p>
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              to="/users"
              className="p-8 rounded-lg transition-all no-underline group"
              style={{
                background: 'rgba(8, 42, 73, 0.6)',
                border: '1px solid transparent',
                color: '#ffffff',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div className="flex items-center gap-4 mb-4">
                {React.createElement(FaUserShield as any, { style: { fontSize: '32px', color: '#6894DE' } })}
                <h2 className="text-xl font-semibold" style={{ color: '#ffffff' }}>المستخدمين</h2>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>إدارة المستخدمين والصلاحيات</p>
            </Link>
          )}
        </div>
      </main>

      {showChangePasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--navbar-bg)' }}>
              تغيير كلمة المرور
            </h2>
            {changePasswordSuccess ? (
              <p className="text-green-600 py-4">تم تغيير كلمة المرور بنجاح.</p>
            ) : (
              <form onSubmit={handleChangePasswordSubmit}>
                {changePasswordError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm">
                    {changePasswordError === 'Current password is incorrect'
                      ? 'كلمة المرور الحالية غير صحيحة'
                      : changePasswordError}
                  </div>
                )}
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">كلمة المرور الحالية</label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0"
                      style={{ borderColor: 'var(--border-main)', '--tw-ring-color': 'var(--navbar-border)' } as React.CSSProperties}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-700"
                      aria-label={showCurrentPassword ? 'إخفاء' : 'إظهار'}
                    >
                      {showCurrentPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.98 8.88A11.45 11.45 0 0 1 12 7c3.21 0 6.16 1.6 8.02 4.12a.75.75 0 0 1 0 .76A11.45 11.45 0 0 1 12 17c-3.21 0-6.16-1.6-8.02-4.12a.75.75 0 0 1 0-.76zM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" /><path d="M2.22 2.22a.75.75 0 0 1 1.06 0l18 18a.75.75 0 1 1-1.06 1.06L2.22 3.28a.75.75 0 0 1 0-1.06z" /></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" /></svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">كلمة المرور الجديدة</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0"
                      style={{ borderColor: 'var(--border-main)', '--tw-ring-color': 'var(--navbar-border)' } as React.CSSProperties}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-700"
                      aria-label={showNewPassword ? 'إخفاء' : 'إظهار'}
                    >
                      {showNewPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.98 8.88A11.45 11.45 0 0 1 12 7c3.21 0 6.16 1.6 8.02 4.12a.75.75 0 0 1 0 .76A11.45 11.45 0 0 1 12 17c-3.21 0-6.16-1.6-8.02-4.12a.75.75 0 0 1 0-.76zM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" /><path d="M2.22 2.22a.75.75 0 0 1 1.06 0l18 18a.75.75 0 1 1-1.06 1.06L2.22 3.28a.75.75 0 0 1 0-1.06z" /></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" /></svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">6 أحرف على الأقل</p>
                </div>
                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">تكرار كلمة المرور الجديدة</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0"
                      style={{ borderColor: 'var(--border-main)', '--tw-ring-color': 'var(--navbar-border)' } as React.CSSProperties}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-700"
                      aria-label={showConfirmPassword ? 'إخفاء' : 'إظهار'}
                    >
                      {showConfirmPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.98 8.88A11.45 11.45 0 0 1 12 7c3.21 0 6.16 1.6 8.02 4.12a.75.75 0 0 1 0 .76A11.45 11.45 0 0 1 12 17c-3.21 0-6.16-1.6-8.02-4.12a.75.75 0 0 1 0-.76zM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" /><path d="M2.22 2.22a.75.75 0 0 1 1.06 0l18 18a.75.75 0 1 1-1.06 1.06L2.22 3.28a.75.75 0 0 1 0-1.06z" /></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" /></svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePasswordModal(false);
                      setChangePasswordError('');
                    }}
                    className="change-password-cancel px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={changePasswordLoading}
                    className="px-4 py-2 rounded-md text-white font-medium disabled:opacity-60"
                    style={{ background: 'var(--button-gradient)', color: 'var(--button-text)' }}
                  >
                    {changePasswordLoading ? 'جاري الحفظ...' : 'حفظ'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;


